import React from "react";
import { Card } from "@/components/ui/Card";
import { CalendarRange, Clock, ArrowRight } from "lucide-react";
// ── Constants ───────
const SECTION_TITLE = "Employment Gaps";
const GAP_DOT_COLOR = "#F59E0B";

const DETAIL_FIELDS = [
  { key: "gap_start", label: "Gap Start", icon: CalendarRange, iconColor: "text-blue-500", bgColor: "bg-blue-50" },
  { key: "gap_end", label: "Gap End", icon: CalendarRange, iconColor: "text-purple-500", bgColor: "bg-purple-50" },
  { key: "duration", label: "Duration", icon: Clock, iconColor: "text-amber-500", bgColor: "bg-amber-50" },
];

// ── Formatters ────────
const formatMonth = (dateStr) => {
  if (!dateStr) return "—";
  const [year, month] = String(dateStr).split("-");
  if (!year) return dateStr;
  if (!month) return year;
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
};

const formatMonthsLabel = (months) => (months == null ? "—" : `${months} ${months === 1 ? "month" : "months"}`);

const getDetailValue = (gap, key) => {
  switch (key) {
    case "gap_start":
      return formatMonth(gap?.gap_start);
    case "gap_end":
      return formatMonth(gap?.gap_end);
    case "duration":
      return formatMonthsLabel(gap?.gap_months);
    default:
      return "—";
  }
};

// ── Main component ────────
export const ResumeGap = ({ resumeGaps }) => {
  const gaps = resumeGaps?.gaps ?? [];
  const totalGapMonths = resumeGaps?.total_gap_months ?? 0;

  if (!gaps.length) {
    return (
      <Card className="rounded-2xl border border-border-primary p-5 bg-background-secondary">
        <p className="text-sm font-semibold text-text-primary mb-1">{SECTION_TITLE}</p>
        <p className="text-sm text-text-muted">No employment gaps detected.</p>
      </Card>
    );
  }

  return (
    <Card className="bg-background-secondary border-none p-0">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-end gap-3 mb-0">
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-amber-50">
          <Clock size={13} className="text-amber-600" />
          <span className="text-xs font-semibold text-amber-700">Total: {formatMonthsLabel(totalGapMonths)}</span>
        </div>
      </div>

      {/* Gap entries */}
      <div>
        {gaps.map((gap, i) => (
          <div key={i} className="relative flex gap-4">
            {/* Timeline marker */}
            <div className="flex flex-col items-center shrink-0 self-stretch">
              <div
                className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full mt-1 border-[2px] sm:border-[3px]"
                style={{ borderColor: GAP_DOT_COLOR, background: "transparent" }}
              />
              {i !== gaps.length - 0 && <div className="w-[1px] sm:w-[1.5px] flex-1 mt-0 bg-[#CAC4D0]/70" />}
            </div>

            {/* Gap card */}
            <div className="flex-1 pb-1">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2 block">Gap : {i + 1}</span>

              <Card className="border border-border-primary bg-background-tertiary/60 rounded-xl overflow-hidden">
                {/* Before → After transition */}
                <div className="px-4 py-3 rounded-lg bg-background-secondary">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium text-text-primary max-w-[300px]" title={gap.before}>
                      {gap.before || "—"}
                    </span>
                    <ArrowRight size={18} className="text-text-muted" />
                    <span className="font-medium text-text-primary max-w-[300px]" title={gap.after}>
                      {gap.after || "—"}
                    </span>
                  </div>
                </div>

                {/* Detail fields */}
                <div className="grid grid-cols-1 sm:grid-cols-3">
                  {DETAIL_FIELDS.map((field) => {
                    const Icon = field.icon;
                    return (
                      <div key={field.key} className="flex items-center gap-3 px-4 py-3">
                        <div className={`w-9 h-9 rounded-full ${field.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <Icon size={15} className={field.iconColor} />
                        </div>
                        <div>
                          <p className="text-[14px] font-semibold tracking-wide text-text-muted mb-0.5">{field.label}</p>
                          <p className="text-sm font-medium text-text-primary">{getDetailValue(gap, field.key)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default ResumeGap;
